// React
import React, { useState, useEffect } from 'react';

// Packages

// Utilities
import { useElseware, useLabels } from 'utilities/hooks';
import { useInitiativeDataStore } from 'utilities/store';

// Components
import { InputWrapper, FormFields } from 'components/_inputs';

const ActivityDetails = ({
    tagging,
    fields = null,
    getItemData = () => {},
    initComponent,
    form,
    addItemCallback = () => {},
    editItemCallback = () => {},
}) => {
    // ///////////////////
    // STORES
    // ///////////////////

    const { utilities } = useInitiativeDataStore();

    // ///////////////////
    // HOOKS
    // ///////////////////

    const { object } = useLabels();
    const { ewUpdate, ewCreate } = useElseware();

    // ///////////////////
    // STATE
    // ///////////////////

    const [customDates, setCustomDates] = useState(false);

    // ///////////////////
    // METHODS
    // ///////////////////

    function getDefaultItemData(formData) {
        const {
            Things_To_Do_Description__c,
            Things_To_Do__c,
            Dates,
            Dates_Toggle,
        } = formData;

        // Activity period dates should fall back to the grant period dates if Dates_Toggle is false
        const activityPeriodDates = Dates_Toggle
            ? Dates
            : {
                  from: utilities.initiative.get()?.Grant_Start_Date__c,
                  to: utilities.initiative.get()?.Grant_End_Date__c,
              };

        // Data for sf
        return {
            Things_To_Do__c,
            Things_To_Do_Description__c,
            Activity_Dates_Equals_Initiative_Dates__c: !Dates_Toggle,
            Activity_Start_Date__c: activityPeriodDates?.from,
            Activity_End_Date__c: activityPeriodDates?.to,
        };
    }
    async function addItem(formData, setValue) {
        try {
            const { data: itemData } = await ewCreate(
                'initiative-activity/initiative-activity-and-children',
                {
                    ...getDefaultItemData(formData),
                    ...getItemData(formData),
                    Activity_Type__c: formData?.Choice?.Name,
                    Initiative__c: utilities.initiative.get().Id,
                }
            );
            // Update main data
            utilities.updateInitiativeData('_activities', itemData);
            // Tagging
            await tagging.submit(formData, itemData);
            // Update form data
            setValue('Activity___Id', itemData?.Id);
            // Callback
            addItemCallback(itemData);
        } catch (error) {
            console.log(error);
            return error;
        }
    }

    async function editItem(formData) {
        const { Activity___Id } = formData;
        try {
            const {
                data: itemData,
            } = await ewUpdate(
                'initiative-activity/initiative-activity-and-children',
                Activity___Id,
                { ...getDefaultItemData(formData), ...getItemData(formData) }
            );

            // Update main data
            utilities.updateInitiativeData('_activities', itemData);

            // Tagging
            await tagging.submit(formData, itemData);

            // Callback
            editItemCallback(itemData);
        } catch (error) {
            console.log(error);
            return error;
        }
    }

    // ///////////////////
    // FIELDS
    // ///////////////////

    function allFields() {
        return [
            {
                type: 'Text',
                name: 'Things_To_Do__c',
                object: object.config('Initiative_Activity__c.Things_To_Do__c'),
                maxLength: 200,
            },
            {
                type: 'LongText',
                name: 'Things_To_Do_Description__c',
                object: object.config(
                    'Initiative_Activity__c.Things_To_Do_Description__c'
                ),
                maxLength: 400,
            },
            {
                type: 'Toggle',
                name: 'Dates_Toggle',
                object: {
                    ...object.config('Initiative_Activity__c.Dates_Toggle'),
                    subLabel: `${object.subLabel(
                        'Initiative_Activity__c.Dates_Toggle'
                    )} (${utilities.initiative.get()?.Grant_Start_Date__c} - ${
                        utilities.initiative.get()?.Grant_End_Date__c
                    })`,
                },
                defaultValue: customDates,
                onWatch(event) {
                    setCustomDates(event);
                },
                nested: {
                    fields() {
                        return [
                            {
                                type: 'DateRange',
                                name: 'Dates',
                                object: object.config(
                                    'Initiative_Activity__c.Dates'
                                ),
                                required: customDates,
                                fromLabel: object.label(
                                    'Initiative_Activity__c.Activity_Start_Date__c'
                                ),
                                toLabel: object.label(
                                    'Initiative_Activity__c.Activity_End_Date__c'
                                ),
                            },
                        ];
                    },
                    isActive() {
                        return customDates;
                    },
                },
            },

            ...(fields ? fields() : []),
            ...(tagging.fields?.length > 0
                ? [
                      {
                          type: 'Section',
                      },
                      ...tagging.fields,
                  ]
                : []),
        ];
    }

    // ///////////////////
    // EFFECTS
    // ///////////////////

    useEffect(() => {
        initComponent({
            fields: allFields(),
            methods: {
                add: { details: addItem },
                edit: { details: editItem },
            },
        });
    }, [customDates]);

    // ///////////////////
    // RETURN
    // ///////////////////

    return (
        <InputWrapper>
            <FormFields
                {...{
                    fields: allFields(),
                    form,
                    theme: 'teal',
                }}
            />
        </InputWrapper>
    );
};

export default ActivityDetails;
